'use client'

import { clsx } from 'clsx'
import { type ComponentPropsWithoutRef, type ElementType, type FC } from 'react'

import { type GkAirRecommendedProductDataWeb } from '@redteclab/api/clients/crosssellers-proxy-service'
import { type Metadata } from '@redteclab/api/clients/retail-media-xsell-service'

import { AD_SERVER_XSELL_PRODUCT_ID_DATA_ATTR } from '../../../ad-server'
import { removeDiscountFromGkAirRecommendedProduct } from '../../../experiments/discount-experiement-fr/removeDiscountFromCrossSellProduct'
import { useGetExperiment } from '../../../experiments/hooks'
import { EXPERIMENT_NAME_DISCOUNT_REMOVAL_FR } from '../../../experiments/model/EXPERIMENT_NAME'
import { EXPERIMENT_VARIATION } from '../../../experiments/model/EXPERIMENT_VARIATION'
import { useGkAirAreaContext } from '../GkAirAreaProvider'

import { GkAirProductImage } from './GkAirProductImage'
import { GkAirProductName } from './GkAirProductName'
import { GkAirProductPrice } from './GkAirProductPrice'
import { GkAirProductQuantity } from './GkAirProductQuantity'
import { GkAirProductRating } from './GkAirProductRating'

type GkAirHorizontalSliderItemProps = ComponentPropsWithoutRef<'section'> & {
  product?: GkAirRecommendedProductDataWeb
  slots?: {
    image?: ElementType
  }
}

export const GkAirProductCard: FC<GkAirHorizontalSliderItemProps> = ({
  className,
  product: productProp,
  slots,
  ...rest
}) => {
  let product = productProp
  const ImageComponent = slots?.image ?? GkAirProductImage
  const { metadata } = useGkAirAreaContext()
  const metadataContent = product?.UID
    ? metadata?.[product.UID as keyof Metadata]
    : undefined

  const discountExperimentFr = useGetExperiment(
    EXPERIMENT_NAME_DISCOUNT_REMOVAL_FR,
  )

  if (
    product &&
    discountExperimentFr.isEnabled &&
    discountExperimentFr.variant === EXPERIMENT_VARIATION.V1
  ) {
    product = removeDiscountFromGkAirRecommendedProduct(product)
  }

  const adServerProps = {
    /*
     * Re-ranked / injected elements from ad server needs to be marked here
     * They're being grabbed in ProductAvailableGkAirArea.tsx
     * and pushed into ad server data layer
     */
    [AD_SERVER_XSELL_PRODUCT_ID_DATA_ATTR]:
      metadataContent && product?.UID ? product.UID : undefined,
  }

  return (
    <section
      className={clsx(
        'mb-2 flex flex-1 flex-col overflow-hidden rounded-2xl bg-light-primary-low shadow-m',
        className,
      )}
      data-product-id={product?.UID}
      {...adServerProps}
      {...rest}
    >
      <ImageComponent product={product} />
      <div className="flex flex-col gap-1 px-2 py-2.5">
        <GkAirProductName product={product} />
        <div className="flex-1 justify-between">
          <GkAirProductRating product={product} />
          <GkAirProductQuantity product={product} />
          <GkAirProductPrice product={product} />
        </div>
      </div>
    </section>
  )
}
